/**
 * Stripe processing fee, computed client-side for transparent display before a
 * charge is confirmed. Mirrors the edge-function gross-up in
 * `supabase/functions/utils/stripe-fee.ts` so the figure the parent sees
 * matches what the backend charges.
 *
 * Gross-up: if we charge X, Stripe keeps X * rate + $0.30, leaving `amount`.
 *   X = (amount + 0.30) / (1 - rate),  fee = X - amount
 */

const CARD_FEE_RATE = 0.029;
const LINK_BANK_FEE_RATE = 0.026;

export const CARD_FEE_LABEL = '2.9% + $0.30';
export const LINK_BANK_FEE_LABEL = '2.6% + $0.30';

/**
 * A saved card whose brand is `link` is a bank payment made through Stripe Link
 * (Instant Bank Payments). Stripe returns it as a card with a placeholder last4
 * of `0000`, and prices it at 2.6% + $0.30 instead of the card rate. A real card
 * saved in Link keeps its own brand (visa, mastercard, ...).
 */
export function isLinkBankBrand(brand: string | null | undefined): boolean {
  return (brand ?? '').toLowerCase() === 'link';
}

/** Rate label for the card that will be charged (`null` = a newly entered card). */
export function processingFeeLabel(brand: string | null | undefined): string {
  return isLinkBankBrand(brand) ? LINK_BANK_FEE_LABEL : CARD_FEE_LABEL;
}

/** Unrounded fee in dollars, for flows that round at the charge. */
export function calculateProcessingFee(amount: number, brand?: string | null): number {
  const rate = isLinkBankBrand(brand) ? LINK_BANK_FEE_RATE : CARD_FEE_RATE;
  const totalWithFee = (amount + 0.3) / (1 - rate);
  return totalWithFee - amount;
}

export function calculateProcessingFeeCents(amountCents: number, brand?: string | null): number {
  if (amountCents <= 0) return 0;
  return Math.max(0, Math.round(calculateProcessingFee(amountCents / 100, brand) * 100));
}

export function calculateCardProcessingFeeCents(amountCents: number): number {
  return calculateProcessingFeeCents(amountCents, null);
}

/** How a saved `link` card is named to parents: its last4 (0000) is a placeholder. */
export const LINK_BANK_CARD_NAME = 'Bank account via Link';

/** Label for a saved card in a payment-method picker, e.g. "visa •••• 4242". */
export function savedCardLabel(brand: string | null | undefined, last4: string | null | undefined): string {
  return isLinkBankBrand(brand) ? LINK_BANK_CARD_NAME : `${brand ?? 'Card'} •••• ${last4 ?? '••••'}`;
}
